import cx from 'classnames';
import { motion } from 'framer-motion';
import React, { forwardRef } from 'react';

import * as styles from './Typography.module.css';

const H = ({
	children,
	level,
	as,
	spacing,
	className = '',
	align,
	color,
}: {
	children: React.ReactNode;
	level: 0 | 1 | 2 | 3 | 4 | 5 | 6;
	as?: React.ElementType;
	spacing?: 'none' | 'sm' | 'md' | 'lg' | 'xl';
	align?: string;
	justify?: 'left' | 'center' | 'right';
	color?: 'black' | 'blue';
	className?: string;
}): JSX.Element => {
	const Component = React.useMemo(() => {
		if (as) return as;
		return `h${level || 1}`;
	}, [level, as]);

	const componentClassName = cx(styles.heading, {
		h0: level === 0,
		h1: level === 1,
		h2: level === 2,
		h3: level === 3,
		h4: level === 4,
		h5: level === 5,
		h6: level === 6,
		[styles[`${spacing}Spacing`]]: spacing,
		[styles[`${align}Align`]]: align,
		[styles[`${color}Color`]]: color,
	});

	return (
		<Component
			className={cx(componentClassName, {
				[className]: !!className,
			})}>
			{children}
		</Component>
	);
};

export const MotionH = ({
	children,
	level,
	as,
	spacing,
	className = '',
	align,
	color,
}: {
	children: React.ReactNode;
	level: 0 | 1 | 2 | 3 | 4 | 5 | 6;
	as?: React.ElementType;
	spacing?: 'none' | 'sm' | 'md' | 'lg' | 'xl';
	className?: string;
	align?: 'left' | 'center' | 'right';
	color?: 'black' | 'blue';
}): JSX.Element => {
	const Component = React.useMemo(() => {
		if (as) return as;
		return `h${level || 1}`;
	}, [level, as]);

	const AninamtedComponent = motion(
		forwardRef((props, ref) => <Component {...props} ref={ref}></Component>)
	);

	const componentClassName = cx(styles.heading, {
		h0: level === 0,
		h1: level === 1,
		h2: level === 2,
		h3: level === 3,
		h4: level === 4,
		h5: level === 5,
		h6: level === 6,
		[styles[`${spacing}Spacing`]]: spacing,
		[styles[`${align}Align`]]: align,
		[styles[`${color}Color`]]: color,
	});

	return (
		<AninamtedComponent
			className={cx(componentClassName, className)}
			initial="hidden"
			whileInView="visible"
			viewport={{ once: true, amount: 0.6 }}
			transition={{ duration: 0.35 }}
			variants={{
				visible: { opacity: 1, translateY: 0 },
				hidden: { opacity: 0, translateY: '1rem' },
			}}>
			{children}
		</AninamtedComponent>
	);
};

export default H;
